import React, { useState, useEffect } from "react";
import Box from "@mui/joy/Box";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import Input from "@mui/joy/Input";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import FormControl from "@mui/joy/FormControl";
import Button from "@mui/joy/Button";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import LinearProgress from "@mui/joy/LinearProgress";
import Snackbar from "@mui/joy/Snackbar";
import { gql, useQuery, useMutation } from "@apollo/client";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

// Interface for fee data
interface FeeData {
  id: string;
  feeClass: string;
  province: string;
  fee: number;
  license: number;
  admin: number;
  platform: number;
  thirdParty01: number;
  thirdParty02: number;
  thirdParty03: number;
  thirdParty04: number;
  thirdParty05: number;
}

// GraphQL query for license fees
const LICENSE_FEES_QUERY = gql`
  query LicenseFees($province: String) {
    licenseFees(province: $province) {
      id
      feeClass
      province
      fee
      license
      admin
      platform
      thirdParty01
      thirdParty02
      thirdParty03
      thirdParty04
      thirdParty05
      monthlyPenalty
    }
  }
`;

// GraphQL mutation for updating license fee
const UPDATE_LICENSE_FEE_MUTATION = gql`
  mutation UpdateLicenseFee(
    $id: ID!
    $license: Float!
    $admin: Float!
    $platform: Float!
    $thirdParty01: Float!
    $thirdParty02: Float!
    $thirdParty03: Float!
    $thirdParty04: Float!
    $thirdParty05: Float!
  ) {
    updateLicenseFee(
      input: {
        id: $id
        license: $license
        admin: $admin
        platform: $platform
        thirdParty01: $thirdParty01
        thirdParty02: $thirdParty02
        thirdParty03: $thirdParty03
        thirdParty04: $thirdParty04
        thirdParty05: $thirdParty05
      }
    ) {
      licenseFee {
        id
        feeClass
        province
        fee
        license
        admin
        platform
        thirdParty01
        thirdParty02
        thirdParty03
        thirdParty04
        thirdParty05
      }
    }
  }
`;

// GraphQL mutation for bulk upload CSV
const BULK_UPLOAD_CSV_MUTATION = gql`
  mutation BulkUploadCsv($file: Upload!) {
    bulkUploadCsv(input: { file: $file }) {
      success
      message
      processedRows
    }
  }
`;

// Group data by fee class
const groupDataByFeeClass = (data: FeeData[]): Record<string, FeeData[]> => {
  return data.reduce((groups, item) => {
    const feeClassName = item.feeClass;
    if (!groups[feeClassName]) {
      groups[feeClassName] = [];
    }
    groups[feeClassName].push(item);
    return groups;
  }, {} as Record<string, FeeData[]>);
};

// Expandable row component for fee type groups
const FeeTypeRow = ({
  feeClassName,
  data,
  isExpanded,
  onToggle,
}: {
  feeClassName: string;
  data: FeeData[];
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<FeeData>>({});

  const [updateLicenseFee, { loading: updating }] = useMutation(
    UPDATE_LICENSE_FEE_MUTATION,
    {
      refetchQueries: [LICENSE_FEES_QUERY],
      onCompleted: () => {
        setEditingRow(null);
        setEditData({});
      },
      onError: (error) => {
        console.error("Update error:", error);
      },
    }
  );

  const handleEdit = (row: FeeData) => {
    setEditingRow(row.id);
    setEditData({ ...row });
  };

  const handleSave = () => {
    if (!editingRow || !editData) return;

    updateLicenseFee({
      variables: {
        id: editingRow,
        license: editData.license,
        admin: editData.admin,
        platform: editData.platform,
        thirdParty01: editData.thirdParty01,
        thirdParty02: editData.thirdParty02,
        thirdParty03: editData.thirdParty03,
        thirdParty04: editData.thirdParty04,
        thirdParty05: editData.thirdParty05,
      },
    });
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditData({});
  };

  const handleInputChange = (field: keyof FeeData, value: string) => {
    setEditData((prev) => ({
      ...prev,
      [field]: field === "province" ? value : parseFloat(value) || 0,
    }));
  };

  return (
    <>
      {/* Main row showing only fee type */}
      <tr>
        <td>
          <IconButton
            aria-label="expand row"
            variant="plain"
            color="neutral"
            size="sm"
            onClick={onToggle}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
        <td>
          <Typography level="body-sm" sx={{ fontWeight: "600" }}>
            {feeClassName}
          </Typography>
        </td>
        <td colSpan={12}></td>
      </tr>
      {isExpanded && (
        <>
          {/* Show all provinces for this fee type */}
          {data.map((row, index) => {
            const isEditing = editingRow === row.id;
            const currentData = isEditing ? editData : row;

            return (
              <tr key={`${feeClassName}-${row.province}-${index}`}>
                <td></td>
                <td></td>
                <td>
                  <Typography level="body-sm" sx={{ fontWeight: "600" }}>
                    {row.province}
                  </Typography>
                </td>
                <td>
                  <Typography level="body-sm" sx={{ fontWeight: "600" }}>
                    {row.fee}
                  </Typography>
                </td>
                <td>
                  {isEditing ? (
                    <>
                      {/* @ts-ignore */}
                      <Input
                        value={currentData.license || ""}
                        onChange={(e) =>
                          handleInputChange("license", e.target.value)
                        }
                        size="sm"
                        type="number"
                        sx={{ minWidth: 80 }}
                      />
                    </>
                  ) : (
                    <Typography level="body-sm">{row.license}</Typography>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <>
                      {/* @ts-ignore */}
                      <Input
                        value={currentData.admin || ""}
                        onChange={(e) =>
                          handleInputChange("admin", e.target.value)
                        }
                        size="sm"
                        type="number"
                        sx={{ minWidth: 80 }}
                      />
                    </>
                  ) : (
                    <Typography level="body-sm">{row.admin}</Typography>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <>
                      {/* @ts-ignore */}
                      <Input
                        value={currentData.platform || ""}
                        onChange={(e) =>
                          handleInputChange("platform", e.target.value)
                        }
                        size="sm"
                        type="number"
                        sx={{ minWidth: 80 }}
                      />
                    </>
                  ) : (
                    <Typography level="body-sm">{row.platform}</Typography>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <>
                      {/* @ts-ignore */}
                      <Input
                        value={currentData.thirdParty01 || ""}
                        onChange={(e) =>
                          handleInputChange("thirdParty01", e.target.value)
                        }
                        size="sm"
                        type="number"
                        sx={{ minWidth: 80 }}
                      />
                    </>
                  ) : (
                    <Typography level="body-sm">{row.thirdParty01}</Typography>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <>
                      {/* @ts-ignore */}
                      <Input
                        value={currentData.thirdParty02 || ""}
                        onChange={(e) =>
                          handleInputChange("thirdParty02", e.target.value)
                        }
                        size="sm"
                        type="number"
                        sx={{ minWidth: 80 }}
                      />
                    </>
                  ) : (
                    <Typography level="body-sm">{row.thirdParty02}</Typography>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <>
                      {/* @ts-ignore */}
                      <Input
                        value={currentData.thirdParty03 || ""}
                        onChange={(e) =>
                          handleInputChange("thirdParty03", e.target.value)
                        }
                        size="sm"
                        type="number"
                        sx={{ minWidth: 80 }}
                      />
                    </>
                  ) : (
                    <Typography level="body-sm">{row.thirdParty03}</Typography>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <>
                      {/* @ts-ignore */}
                      <Input
                        value={currentData.thirdParty04 || ""}
                        onChange={(e) =>
                          handleInputChange("thirdParty04", e.target.value)
                        }
                        size="sm"
                        type="number"
                        sx={{ minWidth: 80 }}
                      />
                    </>
                  ) : (
                    <Typography level="body-sm">{row.thirdParty04}</Typography>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <>
                      {/* @ts-ignore */}
                      <Input
                        value={currentData.thirdParty05 || ""}
                        onChange={(e) =>
                          handleInputChange("thirdParty05", e.target.value)
                        }
                        size="sm"
                        type="number"
                        sx={{ minWidth: 80 }}
                      />
                    </>
                  ) : (
                    <Typography level="body-sm">{row.thirdParty05}</Typography>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="success"
                        onClick={handleSave}
                        disabled={updating}
                      >
                        {updating ? (
                          <CircularProgress size="sm" />
                        ) : (
                          <CheckIcon />
                        )}
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={handleCancel}
                        disabled={updating}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={() => handleEdit(row)}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                </td>
              </tr>
            );
          })}
        </>
      )}
    </>
  );
};

// Main table component
const LicenseFeeTable = ({
  selectedProvince,
  onDataChange,
}: {
  selectedProvince: string;
  onDataChange: (data: FeeData[]) => void;
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { data, loading, error, refetch } = useQuery(LICENSE_FEES_QUERY, {
    variables: {
      province: selectedProvince || undefined,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (wsData.type === "license_fee_updated") {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  const toggleRow = (feeClassName: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(feeClassName)) {
      newExpandedRows.delete(feeClassName);
    } else {
      newExpandedRows.add(feeClassName);
    }
    setExpandedRows(newExpandedRows);
  };

  // Get the license fees data from GraphQL response
  const licenseFees = data?.licenseFees || [];

  // Notify parent component of data changes
  useEffect(() => {
    onDataChange(licenseFees);
  }, [licenseFees, onDataChange]);

  // Group data by fee class
  const groupedData = groupDataByFeeClass(licenseFees);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography level="body-lg" color="danger">
          Error loading data: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Sheet
      className="OrderTableContainer"
      variant="outlined"
      sx={{
        display: { xs: "none", sm: "initial" },
        width: "100%",
        borderRadius: "sm",
        flexShrink: 1,
      }}
    >
      <Table
        aria-labelledby="tableTitle"
        hoverRow
        sx={{
          "--TableCell-headBackground": "var(--joy-palette-background-level1)",
          "--Table-headerUnderlineThickness": "1px",
          "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
          "--TableCell-paddingY": "4px",
          "--TableCell-paddingX": "8px",
        }}
      >
        <thead>
          <tr>
            <th style={{ width: "3%", padding: "12px 6px" }}></th>
            <th style={{ width: "8%", padding: "12px 6px" }}>Fee Type</th>
            <th style={{ width: "12%", padding: "12px 6px" }}>Province</th>
            <th style={{ width: "8%", padding: "12px 6px" }}>Fee</th>
            <th style={{ width: "8%", padding: "12px 6px" }}>License</th>
            <th style={{ width: "8%", padding: "12px 6px" }}>Admin</th>
            <th style={{ width: "8%", padding: "12px 6px" }}>Trafica Fee</th>
            <th style={{ width: "8%", padding: "12px 6px" }}>3rd Party 1</th>
            <th style={{ width: "8%", padding: "12px 6px" }}>3rd Party 2</th>
            <th style={{ width: "8%", padding: "12px 6px" }}>3rd Party 3</th>
            <th style={{ width: "7%", padding: "12px 6px" }}>3rd Party 4</th>
            <th style={{ width: "7%", padding: "12px 6px" }}>3rd Party 5</th>
            <th style={{ width: "8%", padding: "12px 6px" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(groupedData).length === 0 ? (
            <tr>
              <td colSpan={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 4,
                  }}
                >
                  <Typography level="body-lg" color="neutral">
                    No data found matching your filters.
                  </Typography>
                </Box>
              </td>
            </tr>
          ) : (
            Object.entries(groupedData).map(([feeClassName, feeData]) => (
              <React.Fragment key={feeClassName}>
                <FeeTypeRow
                  feeClassName={feeClassName}
                  data={feeData}
                  isExpanded={expandedRows.has(feeClassName)}
                  onToggle={() => toggleRow(feeClassName)}
                />
              </React.Fragment>
            ))
          )}
        </tbody>
      </Table>
    </Sheet>
  );
};

// Bulk Upload Modal Component
const BulkUploadModal = ({
  open,
  onClose,
  onUploadSuccess,
  onShowSnackbar,
}: {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
  onShowSnackbar: (
    message: string,
    color?: "success" | "danger" | "warning"
  ) => void;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    message: string;
    processedRows: number;
  } | null>(null);

  const [bulkUploadCsv] = useMutation(BULK_UPLOAD_CSV_MUTATION, {
    onCompleted: (data) => {
      setUploading(false);
      setUploadResult(data.bulkUploadCsv);
      if (data.bulkUploadCsv.success) {
        onUploadSuccess();
      }
    },
    onError: (error) => {
      setUploading(false);
      setUploadResult({
        success: false,
        message: error.message,
        processedRows: 0,
      });
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file: File) => {
    // Validate file type
    if (!file.name.toLowerCase().endsWith(".csv")) {
      onShowSnackbar("Please select a CSV file only.", "warning");
      return;
    }
    setSelectedFile(file);
    setUploadResult(null);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      processFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      await bulkUploadCsv({
        variables: {
          file: selectedFile,
        },
      });
      setUploadProgress(100);
      clearInterval(progressInterval);
    } catch (error) {
      clearInterval(progressInterval);
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setUploadProgress(0);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        sx={{
          maxWidth: 500,
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <ModalClose />
        <Typography level="h4" component="h2" sx={{ mb: 2 }}>
          Bulk Upload CSV
        </Typography>

        <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
          Upload a CSV file to update license fee data in bulk. The file should
          match the same format as the exported CSV.
        </Typography>

        <FormControl sx={{ mb: 3 }}>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            Select CSV File
          </Typography>
          <Box
            sx={{
              border: `2px dashed ${isDragOver ? "#0969da" : "#d0d7de"}`,
              borderRadius: "8px",
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: isDragOver ? "#f6f8fa" : "transparent",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: "#0969da",
                backgroundColor: "#f6f8fa",
              },
            }}
            onClick={() => document.getElementById("csv-file-input")?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              id="csv-file-input"
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            {selectedFile ? (
              <Box>
                <CheckCircleIcon sx={{ color: "success.500", mb: 1 }} />
                <Typography level="body-sm" sx={{ fontWeight: "600" }}>
                  {selectedFile.name}
                </Typography>
                <Typography level="body-xs" color="neutral">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
            ) : (
              <Box>
                <UploadFileIcon
                  sx={{
                    fontSize: 48,
                    color: isDragOver ? "#0969da" : "neutral.400",
                    mb: 1,
                  }}
                />
                <Typography level="body-sm" sx={{ fontWeight: "600" }}>
                  {isDragOver
                    ? "Drop CSV file here"
                    : "Click to select or drag CSV file here"}
                </Typography>
                <Typography level="body-xs" color="neutral">
                  Only .csv files are allowed
                </Typography>
              </Box>
            )}
          </Box>
        </FormControl>

        {uploading && (
          <Box sx={{ mb: 3 }}>
            <Typography level="body-sm" sx={{ mb: 1 }}>
              Uploading... {uploadProgress}%
            </Typography>
            <LinearProgress value={uploadProgress} />
          </Box>
        )}

        {uploadResult && (
          <Box
            sx={{
              p: 2,
              borderRadius: "8px",
              backgroundColor: uploadResult.success
                ? "success.50"
                : "danger.50",
              border: `1px solid ${
                uploadResult.success ? "success.200" : "danger.200"
              }`,
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              {uploadResult.success ? (
                <CheckCircleIcon sx={{ color: "success.500", mr: 1 }} />
              ) : (
                <ErrorIcon sx={{ color: "danger.500", mr: 1 }} />
              )}
              <Typography
                level="body-sm"
                sx={{
                  fontWeight: "600",
                  color: uploadResult.success ? "success.700" : "danger.700",
                }}
              >
                {uploadResult.success ? "Upload Successful" : "Upload Failed"}
              </Typography>
            </Box>
            <Typography level="body-sm" sx={{ mb: 1 }}>
              {uploadResult.message}
            </Typography>
            {uploadResult.success && (
              <Typography level="body-xs" color="neutral">
                Processed {uploadResult.processedRows} rows
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            startDecorator={
              uploading ? <CircularProgress size="sm" /> : <CloudUploadIcon />
            }
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

// CSV export function
const exportToCSV = (data: FeeData[]) => {
  // CSV headers matching the fees_class.csv format
  const headers = [
    "Fee Type",
    "Province",
    "Fee",
    "License",
    "Admin",
    "Trafica fee",
    "3rd party 01",
    "3rd party 02",
    "3rd party 03",
    "3rd party 04",
    "3rd party 05",
  ];

  // Convert data to CSV format
  const csvContent = [
    headers.join(","),
    ...data.map((row) =>
      [
        row.feeClass,
        row.province,
        row.fee,
        row.license,
        row.admin,
        row.platform, // This maps to "Trafica fee" in the CSV
        row.thirdParty01,
        row.thirdParty02,
        row.thirdParty03,
        row.thirdParty04,
        row.thirdParty05,
      ].join(",")
    ),
  ].join("\n");

  // Create and download the file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `license_fees_${new Date().toISOString().split("T")[0]}.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Main component
const LicenseFeeCalculations = () => {
  const [selectedProvince, setSelectedProvince] = useState("");
  const [licenseFeesData, setLicenseFeesData] = useState<FeeData[]>([]);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarColor, setSnackbarColor] = useState<
    "success" | "danger" | "warning"
  >("success");

  const handleDataChange = (data: FeeData[]) => {
    setLicenseFeesData(data);
  };

  const showSnackbar = (
    message: string,
    color: "success" | "danger" | "warning" = "success"
  ) => {
    setSnackbarMessage(message);
    setSnackbarColor(color);
    setSnackbarOpen(true);
  };

  const handleExport = () => {
    if (licenseFeesData.length === 0) {
      showSnackbar(
        "No data to export. Please ensure data is loaded.",
        "warning"
      );
      return;
    }
    exportToCSV(licenseFeesData);
    showSnackbar(
      `Successfully exported ${licenseFeesData.length} records to CSV`,
      "success"
    );
  };

  const handleBulkUploadSuccess = () => {
    showSnackbar(
      "CSV uploaded successfully! Data has been updated.",
      "success"
    );
    // Refresh the data after successful upload
    window.location.reload(); // Simple refresh - you could also refetch the query
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          License Fee Calculations
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "flex-end",
          width: "100%",
        }}
      >
        <FormControl sx={{ flex: "0 0 300px" }}>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            Search by Province
          </Typography>
          {/* @ts-ignore */}
          <Input
            placeholder="Search by Province..."
            startDecorator={<SearchIcon />}
            endDecorator={
              selectedProvince && (
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() => setSelectedProvince("")}
                >
                  <ClearIcon />
                </IconButton>
              )
            }
            value={selectedProvince}
            onChange={(event: any) => setSelectedProvince(event.target.value)}
            sx={{ height: "40px" }}
          />
        </FormControl>

        <Box
          sx={{
            flex: "1",
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <Button
            variant="solid"
            color="primary"
            startDecorator={<CloudUploadIcon />}
            onClick={() => setBulkUploadOpen(true)}
            sx={{
              borderRadius: "8px",
              fontWeight: "600",
              px: 3,
              py: 1.5,
              minWidth: 150,
              height: "40px",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Bulk Upload
          </Button>

          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<FileDownloadIcon />}
            onClick={handleExport}
            sx={{
              borderRadius: "8px",
              fontWeight: "600",
              px: 3,
              py: 1.5,
              minWidth: 150,
              height: "40px",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      <LicenseFeeTable
        selectedProvince={selectedProvince}
        onDataChange={handleDataChange}
      />

      <BulkUploadModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onUploadSuccess={handleBulkUploadSuccess}
        onShowSnackbar={showSnackbar}
      />

      <Snackbar
        open={snackbarOpen}
        color={snackbarColor}
        variant="soft"
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={4000}
      >
        {snackbarMessage}
      </Snackbar>
    </>
  );
};

export default LicenseFeeCalculations;
